// Non-overlapping placement for the flying word tiles on a board. Kept as a pure, Phaser-free
// helper so the "tiles never overlap" invariant can be unit-tested (see wordPlacement.test.ts) —
// the previous inline version dropped a tile at the board centre (unchecked, and never recorded)
// whenever random sampling failed, which occasionally let two words overlap.

/** Axis-aligned rectangle, top-left anchored. */
export interface Rect { x: number; y: number; w: number; h: number; }

export interface PlaceBounds { minX: number; maxX: number; minY: number; maxY: number; }

/** Area of the overlap between two rects (0 when they don't intersect). */
function overlapArea(a: Rect, b: Rect): number {
  const ox = Math.max(0, Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x));
  const oy = Math.max(0, Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y));
  return ox * oy;
}

/**
 * Pick a centre (cx, cy) for a w×h box whose centre stays within [minX..maxX]×[minY..maxY] and
 * that does not overlap any rect already in `placed`. Samples random centres and returns the first
 * free one. If none is free within `maxAttempts` (a genuinely crowded board), it returns the
 * LEAST-overlapping centre found rather than a blind fallback, so any unavoidable overlap is
 * minimised. Always returns a result; the caller must push the returned `rect` into `placed` so
 * subsequent boxes avoid this one too.
 */
export function placeBox(
  w: number,
  h: number,
  bounds: PlaceBounds,
  placed: readonly Rect[],
  rand: (min: number, max: number) => number,
  maxAttempts = 200,
): { cx: number; cy: number; rect: Rect } {
  let best: { cx: number; cy: number; rect: Rect } | null = null;
  let bestOverlap = Infinity;

  for (let i = 0; i < maxAttempts; i++) {
    const cx = rand(bounds.minX, bounds.maxX);
    const cy = rand(bounds.minY, bounds.maxY);
    const rect: Rect = { x: cx - w / 2, y: cy - h / 2, w, h };

    let overlap = 0;
    for (const r of placed) {
      overlap += overlapArea(r, rect);
      if (overlap >= bestOverlap) break;   // can't beat the current best — stop early
    }

    if (overlap === 0) return { cx, cy, rect };
    if (overlap < bestOverlap) { bestOverlap = overlap; best = { cx, cy, rect }; }
  }

  // maxAttempts >= 1, so `best` is always set by the first iteration.
  return best!;
}
